import { Table } from '@tanstack/react-table';
import { TableFilter } from '../../shared/filter';
import {
  TableRoofChipList,
  TableRoofFilter
} from '../../../compound/tables/presentation/table/roofs/shared';
import { DataTableResults } from '../../shared/results';
import { DataTableSearch } from '../../shared/search';
import { IconButton, Typography, Box, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import {
  SendMessageIcon,
  DeleteIcon,
  BenefitSetIcon,
  BudgetIcon,
  EditIcon
} from '../../../../icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { enqueueSnackbar } from 'notistack';
import {
  useBulkInvite,
  useBulkInvitePreview,
  useDeleteEmployee,
  useSendEmployeeInvitation
} from '../../../../../queries/employee/mutations';
import {
  EmployeeBulkInviteFormatEnum,
  EmployeeBulkInvitePreviewResponse,
  EmployeeListItem
} from '@pxtra-client-lib-legacy/gen';
import { ConfirmationDialog } from '../../../compound';
import { downloadBlob } from '../../../../util';
import { useQueryClient } from '@tanstack/react-query';
import { getEmployeeInvitationApiErrorMessage } from '../../../../util/apiErrorMessage';
import { useState, useMemo, useEffect } from 'react';
import { useStore } from '../../../../../hooks/useStore';
import { FeedbackAlert, TooltipComponent } from '../../../atomic/feedback';
import { useListBenefitSet } from '../../../../../queries/benefit/benefitSet';
import { usePaymentData } from '../../../../../queries/company';
import { checkHasCompanyBenefitSets } from '../../../../util/pricingModel';

export const EmployeeOverviewToolBar = <T extends EmployeeListItem>({
  table,
  canDeselect = true,
  onBulkInviteProcessingChange
}: {
  table: Table<T>;
  canDeselect?: boolean;
  onBulkInviteProcessingChange?: (processing: boolean) => void;
}) => {
  const selectedRows = table.getSelectedRowModel().rows;
  const hasSelection = selectedRows.length > 0;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { companyStore } = useStore();

  const [isDeletionDialogOpen, setIsDeletionDialogOpen] = useState(false);
  const [isInvitationDialogOpen, setIsInvitationDialogOpen] = useState(false);
  const [bulkPreview, setBulkPreview] = useState<EmployeeBulkInvitePreviewResponse | null>(null);
  const [bulkPdfFormat, setBulkPdfFormat] = useState<EmployeeBulkInviteFormatEnum>('merged_pdf');

  const { data: paymentData } = usePaymentData(companyStore.company?.id);
  const { data: benefitSetData } = useListBenefitSet(
    companyStore.company?.id ?? '',
    paymentData?.pricing_model
  );

  const deletionDisabled = useMemo(
    () =>
      selectedRows.some(
        (row) =>
          !['INACTIVE', 'INVITATION_PENDING_EMAIL', 'INVITATION_PENDING_PDF'].includes(
            row.original.status ?? ''
          )
      ),
    [selectedRows]
  );

  const assignmentDisabled = useMemo(
    () => (benefitSetData?.results.length ?? 0) === 0,
    [benefitSetData?.results.length]
  );

  const hasBenefitSets = checkHasCompanyBenefitSets(
    paymentData?.pricing_model,
    paymentData?.booked_benefits
  );

  const invitationDisabled = useMemo(
    () => selectedRows.some((row) => hasBenefitSets && row.original.next_month_benefit_set == null),
    [selectedRows]
  );

  const hasEmployeesWithNoBudget = useMemo(
    () =>
      selectedRows.some((employee) => employee.original.available_benefit_budget_next_month === 0),
    [selectedRows]
  );

  const hasResignedEmployees = useMemo(
    () => selectedRows.some((row) => row.original.status === 'RESIGNED'),
    [selectedRows]
  );

  const { mutateAsync: sendInvitation } = useSendEmployeeInvitation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      enqueueSnackbar(t('employee:EMPLOYEE_INVITATION_SENT', { count: selectedRows.length }), {
        variant: 'success'
      });
    },
    onError: (error: unknown, variables: { employeeName?: string }) => {
      const apiMessage = getEmployeeInvitationApiErrorMessage(error, t);
      if (apiMessage) {
        enqueueSnackbar(apiMessage, { variant: 'error' });
        return;
      }
      enqueueSnackbar(
        t('employee:ERROR_SENDING_EMPLOYEE_INVITATION_details', {
          employee_name: variables.employeeName,
          error: error instanceof Error ? error.message : 'Unknown error'
        }),
        {
          variant: 'error'
        }
      );
    }
  });

  const { mutateAsync: bulkInvitePreview } = useBulkInvitePreview({});

  const { mutateAsync: bulkInvite, isPending: isBulkInvitePending } = useBulkInvite({
    onSuccess: (blob: Blob) => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      // The endpoint returns 204 (no body) when only mail employees were
      // selected. The OpenAPI client surfaces that as an empty Blob.
      const isPdfDownload = blob && blob.size > 0;
      if (isPdfDownload) {
        const filename =
          bulkPdfFormat === 'zip' ? 'pxtra-einladungen.zip' : 'pxtra-einladungen.pdf';
        downloadBlob(blob, filename);
        enqueueSnackbar(t('employee:BULK_INVITE_PDF_GENERATED'), { variant: 'success' });
      } else {
        enqueueSnackbar(t('employee:BULK_INVITE_MAILS_SENT', { count: selectedRows.length }), {
          variant: 'success'
        });
      }
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      enqueueSnackbar(t('employee:BULK_INVITE_ERROR'), { variant: 'error' });
    }
  });

  useEffect(() => {
    onBulkInviteProcessingChange?.(isBulkInvitePending);
  }, [isBulkInvitePending, onBulkInviteProcessingChange]);

  const { mutateAsync: deleteEmployee } = useDeleteEmployee({
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      enqueueSnackbar(t('employee:EMPLOYEE_DELETED'), { variant: 'success' });
    },
    onError: (error: unknown, variables: { employeeName?: string }) => {
      enqueueSnackbar(
        t('employee:ERROR_DELETING_EMPLOYEE_details', {
          employee_name: variables.employeeName,
          error: error instanceof Error ? error.message : 'Unknown error'
        }),
        {
          variant: 'error'
        }
      );
    }
  });

  const actionItems = useMemo(() => {
    const baseItems = [
      {
        icon: <EditIcon />,
        label: t('company:EDIT_EMPLOYEE'),
        onClick: () => {
          navigate(`/actions/update-employees/1`);
        },
        color: 'primary.main'
      },
      {
        icon: <BudgetIcon />,
        label: t('company:EDIT_BUDGET'),
        onClick: () => {
          navigate(`/actions/update-budget/1`);
        },
        color: 'primary.main'
      },
      {
        color: invitationDisabled ? 'grey.400' : 'primary.main',
        icon: <SendMessageIcon />,
        label: invitationDisabled
          ? t('benefitSet:invitationPossibleIfBenefitSet')
          : t('company:SEND_INVITATION'),
        onClick: async () => {
          setBulkPreview(null);
          setBulkPdfFormat('merged_pdf');
          setIsInvitationDialogOpen(true);
          try {
            const preview = await bulkInvitePreview({
              employee_ids: selectedRows.map((row) => row.id)
            });
            setBulkPreview(preview);
          } catch {
            // Preview failed: keep null, dialog falls back to legacy mail-only
            // copy and the actual invite call surfaces any backend error.
          }
        },
        disabled: invitationDisabled
      },
      {
        color: deletionDisabled ? 'grey.400' : 'error.main',
        icon: <DeleteIcon />,
        label: deletionDisabled ? t('company:DELETE_EMPLOYEE_BLOCKED') : t('common:DELETE'),
        onClick: () => {
          setIsDeletionDialogOpen(true);
        },
        disabled: deletionDisabled
      }
    ];

    if (hasBenefitSets) {
      const benefitSetAssignmentAction = {
        color: assignmentDisabled ? 'grey.400' : 'primary.main',
        icon: <BenefitSetIcon />,
        label: assignmentDisabled
          ? t('benefitSet:assignmentPossibleIfBenefitSet')
          : t('benefitSet:assignBenefitSet'),
        onClick: () => {
          navigate('/actions/benefit-set/assign_benefit_set/1');
        },
        disabled: assignmentDisabled
      };

      return [baseItems[0], baseItems[1], benefitSetAssignmentAction, baseItems[2], baseItems[3]];
    }

    return baseItems;
  }, [
    t,
    navigate,
    invitationDisabled,
    deletionDisabled,
    assignmentDisabled,
    hasBenefitSets,
    bulkInvitePreview,
    selectedRows
  ]);

  const handleInvitationConfirm = async () => {
    const invitableRows = selectedRows.filter(
      (row) => !hasBenefitSets || row.original.next_month_benefit_set != null
    );
    const employeeIds = invitableRows.map((row) => row.id);

    if (bulkPreview && bulkPreview.pdf_count > 0) {
      setIsInvitationDialogOpen(false);
      try {
        await bulkInvite({ employee_ids: employeeIds, format: bulkPdfFormat });
      } finally {
        table.resetRowSelection();
        setBulkPreview(null);
      }
      return;
    }

    setIsInvitationDialogOpen(false);
    await Promise.allSettled(
      invitableRows.map((row) =>
        sendInvitation({
          id: row.id,
          employeeName: `${row.original.first_name} ${row.original.last_name}`
        })
      )
    );
    table.resetRowSelection();
    setBulkPreview(null);
  };

  const handleDeletionConfirm = async () => {
    await Promise.allSettled(
      selectedRows.map((row) =>
        deleteEmployee({
          id: row.id,
          employeeName: `${row.original.first_name} ${row.original.last_name}`
        })
      )
    );

    table.resetRowSelection();
    setIsDeletionDialogOpen(false);
  };

  const hasMixedBulkInvite = !!bulkPreview && bulkPreview.pdf_count > 0;

  let invitationDialogTitle: string;
  if (hasMixedBulkInvite) {
    invitationDialogTitle = t('employee:BULK_INVITE_MIXED_TITLE');
  } else if (hasResignedEmployees) {
    invitationDialogTitle = t('employee:INVITE_RESIGNED_TITLE');
  } else if (hasEmployeesWithNoBudget) {
    invitationDialogTitle = t('employee:INVITE_NO_BUDGET');
  } else {
    invitationDialogTitle = t('company:SEND_INVITATION_TITLE');
  }

  let invitationDialogContentText: string | undefined;
  if (hasMixedBulkInvite) {
    invitationDialogContentText = undefined;
  } else if (hasResignedEmployees) {
    invitationDialogContentText = t('employee:INVITE_RESIGNED_WARNING');
  } else if (hasEmployeesWithNoBudget) {
    invitationDialogContentText = t('employee:INVITATION_WARNING_NO_BUDGET');
  } else {
    invitationDialogContentText = '';
  }

  return (
    <div className="flex items-center min-h-12 justify-between py-4">
      <DataTableResults table={table} canDeselect={canDeselect} />
      <TableRoofChipList
        chips={table.getState().columnFilters.map((filter) => {
          const column = table.getColumn(filter.id);
          const filterOptions = column?.columnDef.meta?.filterOptions;

          let label;
          if (
            Array.isArray(filter.value) &&
            (filter.id === 'available_benefit_budget_next_month' || filter.id === 'entry_date')
          ) {
            const [min, max] = filter.value;
            if (min && max) {
              label = `${min} - ${max}`;
            } else if (min) {
              label = `≥ ${min}`;
            } else if (max) {
              label = `≤ ${max}`;
            } else {
              label = '';
            }
          } else {
            label = String(filter.value);
          }

          if (filterOptions) {
            const option = filterOptions.find((opt) => String(opt.value) === String(filter.value));
            if (option) {
              label = option.text;
            }
          }

          return {
            label: label,
            value: filter.id,
            onDelete: () => {
              table.getColumn(filter.id)?.setFilterValue(undefined);
              table.resetRowSelection();
            }
          };
        })}
        justify="flex-start"
        sx={{ width: '50%' }}
      />
      {hasSelection ? (
        <div className="flex items-center gap-2">
          {actionItems.map((item, index) => {
            const iconButton = (
              <IconButton
                key={item.label + index}
                onClick={item.onClick}
                size="small"
                aria-label={item.label}
                disableRipple
                sx={{
                  color: item.color,
                  '&.Mui-disabled': {
                    color: 'grey.400'
                  }
                }}
                disabled={item.disabled}
              >
                {item.icon}
              </IconButton>
            );

            if (item.disabled && item.label) {
              return (
                <TooltipComponent
                  key={item.label + index}
                  title={item.label}
                  placement="bottom"
                  arrow
                >
                  <span>{iconButton}</span>
                </TooltipComponent>
              );
            }

            return (
              <TooltipComponent
                key={item.label + index}
                title={item.label}
                placement="bottom"
                arrow
              >
                {iconButton}
              </TooltipComponent>
            );
          })}
        </div>
      ) : (
        <>
          <DataTableSearch table={table} />
          <TableRoofFilter renderFilter={() => <TableFilter table={table} />} />
        </>
      )}

      <ConfirmationDialog
        open={isInvitationDialogOpen}
        onClose={() => setIsInvitationDialogOpen(false)}
        onConfirm={handleInvitationConfirm}
        title={invitationDialogTitle}
        width="500px"
        contentComponent={
          hasMixedBulkInvite && bulkPreview ? (
            <Box display="flex" flexDirection="column" gap={2}>
              {bulkPreview.skipped_count > 0 && (
                <Typography variant="body2">
                  {t('employee:BULK_INVITE_SKIPPED_HINT', {
                    skippedCount: bulkPreview.skipped_count
                  })}
                </Typography>
              )}
              <Typography variant="body2">
                {bulkPreview.email_count > 0
                  ? t('employee:BULK_INVITE_MIXED_MESSAGE', {
                      count: bulkPreview.pdf_count,
                      emailCount: bulkPreview.email_count,
                      total: bulkPreview.total
                    })
                  : t('employee:BULK_INVITE_PDF_ONLY_MESSAGE', {
                      count: bulkPreview.pdf_count
                    })}
              </Typography>
              <Box>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  {t('employee:BULK_INVITE_FORMAT_LABEL')}
                </Typography>
                <RadioGroup
                  value={bulkPdfFormat}
                  onChange={(_e, value) => setBulkPdfFormat(value as EmployeeBulkInviteFormatEnum)}
                >
                  <FormControlLabel
                    value="merged_pdf"
                    control={<Radio size="small" />}
                    label={
                      <Typography variant="body2">
                        {t('employee:BULK_INVITE_FORMAT_MERGED')}
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    value="zip"
                    control={<Radio size="small" />}
                    label={
                      <Typography variant="body2">
                        {t('employee:BULK_INVITE_FORMAT_ZIP')}
                      </Typography>
                    }
                  />
                </RadioGroup>
              </Box>
              {hasResignedEmployees && (
                <Typography variant="body2" color="warning.main">
                  {t('employee:INVITE_RESIGNED_WARNING')}
                </Typography>
              )}
              {hasEmployeesWithNoBudget && (
                <FeedbackAlert
                  severity="warning"
                  description={t('employee:INVITATION_WARNING_NO_BUDGET')}
                />
              )}
            </Box>
          ) : undefined
        }
        contentText={invitationDialogContentText}
        variant={hasResignedEmployees ? 'warning' : 'information'}
      />

      <ConfirmationDialog
        open={isDeletionDialogOpen}
        onClose={() => setIsDeletionDialogOpen(false)}
        onConfirm={handleDeletionConfirm}
        title={t('confirm_dialogs:DELETE_TITLE_employees', {
          count: selectedRows.length
        })}
        contentComponent={
          <>
            <Typography variant="body1" marginBottom={2}>
              {t('confirm_dialogs:DELETE_CONTENT_employees', {
                count: selectedRows.length
              })}
            </Typography>
            {selectedRows.map((row) => (
              <Typography variant="caption" key={row.id}>
                {row.original.first_name} {row.original.last_name}
              </Typography>
            ))}
          </>
        }
        variant="delete"
      />
    </div>
  );
};
